/**
 * RootNavigator — boots, decides auth vs app, and hosts both.
 *   booting → probe GET /v1/me (valid session cookie?) → app, else auth.
 *   enterApp()/exitApp() flip the phase after login/logout.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Platform, View } from 'react-native';
import { AuthFlowProvider, AuthPhase } from './authFlowContext';
import ActivationGate from './activationGate';
import { redirectToNotInPlan, redirectToPlatformAuth } from './platformAuthRedirect';
import { isNotInPlan } from './planGate';
import { getCurrentUser, invalidateMeCache, logoutUser } from '../services/auth/auth.service';
import { fetchAccessToken } from '../services/auth/auth.service';
import { useTheme } from '../context/themeContext';
import { useSession } from '../context/sessionContext';

const RootNavigator: React.FC = () => {
  const { theme } = useTheme();
  const { setSession, clear } = useSession();
  const [phase, setPhase] = useState<AuthPhase>('booting');

  // No local login/signup: any non-app state bounces to the centralized SK
  // Platform auth (product=billing). We set 'auth' as a fallback, but on web the
  // redirect navigates away.
  const goToAuth = useCallback(() => {
    setPhase('auth');
    redirectToPlatformAuth();
  }, []);

  const probe = useCallback(async () => {
    try {
      const me = await getCurrentUser();
      const u = me?.data?.user;
      const o = me?.data?.org;
      if (u) {
        setSession(
          { firstName: u.first_name, lastName: u.last_name, email: u.email },
          o ? { id: o.id, name: o.name } : null,
        );
        // Ensure we hold a Bearer token for billing calls after a cold load.
        await fetchAccessToken();
        if (u.email_verified === false) {
          goToAuth();
          return;
        }
        // Signed in, but this org's plan does not include the Billing website
        // (SK Fleet uses Billing underneath without selling it). No list =
        // open. The session is kept — their own console still needs it. Stays
        // on the boot spinner while the browser leaves.
        if (Platform.OS === 'web' && isNotInPlan(me?.data?.subscribed_products)) {
          redirectToNotInPlan();
          return;
        }
        setPhase('app');
        return;
      }
      goToAuth();
    } catch {
      goToAuth();
    }
  }, [setSession, goToAuth]);

  useEffect(() => {
    probe();
  }, [probe]);

  const enterApp = useCallback(() => {
    invalidateMeCache();
    setPhase('app');
  }, []);

  const exitApp = useCallback(() => {
    logoutUser().finally(() => {
      clear();
      goToAuth();
    });
  }, [clear, goToAuth]);

  const flow = useMemo(
    () => ({ phase, enterApp, exitApp, refreshSession: probe }),
    [phase, enterApp, exitApp, probe],
  );

  return (
    <AuthFlowProvider value={flow}>
      {phase === 'app' ? (
        <ActivationGate />
      ) : (
        // booting OR auth (redirecting to the centralized platform auth) → spinner.
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.colors.paper }}>
          <ActivityIndicator color={theme.colors.accent} />
        </View>
      )}
    </AuthFlowProvider>
  );
};

export default RootNavigator;
